import { createTheme, type ThemeOptions } from "@mui/material/styles";
import { rawColor, colors } from "../constants/colors";

export const volcanoThemeOptions: ThemeOptions = {
  name: "volcano",
  palette: {
    mode: "dark",
    primary: { main: rawColor.volcano.main },
    secondary: { main: rawColor.volcano.secondary },
    background: {
      default: rawColor.volcano.background,
      paper: rawColor.volcano.paper,
    },
    text: {
      primary: rawColor.volcano.text.primary,
      secondary: rawColor.volcano.text.secondary,
    },
    action: {
      hover: rawColor.volcano.sidebar.hover,
    },
    error: { main: rawColor.status.error.dark },
    warning: { main: rawColor.status.warning },
    success: { main: rawColor.status.active.green },
    divider: rawColor.volcano.divider,
  },
  sidebar: {
    base: colors.sidebar.volcano.base,
    hover: colors.sidebar.volcano.hover,
    selected: colors.sidebar.volcano.selected,
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.sidebar.base,
          borderRight: `1px solid ${theme.palette.divider}`,
          "& .MuiListItemIcon-root": {
            color: rawColor.volcano.text.secondary,
          },
          "& .MuiListItemText-root": {
            color: rawColor.volcano.text.primary,
          },
          "& .MuiListItemButton-root.Mui-selected": {
            backgroundColor: theme.sidebar.selected,
            "& .MuiListItemIcon-root": {
              color: rawColor.volcano.main,
            },
            "& .MuiListItemText-root": {
              color: rawColor.volcano.main,
            },
          },
          "& .MuiListItemButton-root:hover": {
            backgroundColor: theme.sidebar.hover,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: colors.table.volcano.cell,
          color: rawColor.volcano.text.primary,
        },
        head: {
          backgroundColor: colors.table.volcano.header,
          color: colors.table.volcano.headerText,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: rawColor.volcano.paper,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h5: {
          color: rawColor.volcano.text.primary,
        },
        h6: {
          color: rawColor.volcano.text.primary,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: rawColor.volcano.text.primary,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: rawColor.volcano.text.secondary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "currentColor",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: rawColor.volcano.text.secondary,
          "& .MuiInputBase-input": {
            color: rawColor.volcano.text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: rawColor.volcano.text.secondary,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.volcano.divider,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.volcano.text.secondary,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.volcano.main,
          },
          "& .MuiOutlinedInput-input": {
            color: rawColor.volcano.text.secondary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: rawColor.volcano.text.secondary,
          "&.Mui-focused": {
            color: rawColor.volcano.main,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: rawColor.volcano.text.secondary,
        },
        select: {
          color: rawColor.volcano.text.secondary,
        },
        selectLabel: {
          color: rawColor.volcano.text.secondary,
        },
        selectIcon: {
          color: rawColor.volcano.text.secondary,
        },
        displayedRows: {
          color: rawColor.volcano.text.secondary,
        },
        actions: {
          color: rawColor.volcano.text.secondary,
          "& .MuiSvgIcon-root": {
            color: rawColor.volcano.text.secondary,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiAutocomplete-option": {
            color: rawColor.volcano.text.secondary,
            "&:hover": {
              backgroundColor: rawColor.volcano.sidebar.hover,
            },
            '&[aria-selected="true"]': {
              backgroundColor: rawColor.volcano.sidebar.selected,
              color: rawColor.volcano.main,
              "&:hover": {
                backgroundColor: rawColor.volcano.sidebar.selected,
              },
            },
          },
          "& .MuiAutocomplete-listbox": {
            backgroundColor: rawColor.volcano.paper,
            "& .MuiAutocomplete-option": {
              padding: "8px 16px",
            },
          },
          "& .MuiAutocomplete-option .MuiTypography-root": {
            color: "inherit",
          },
          "& .MuiAutocomplete-option .MuiListItemText-root": {
            color: "inherit",
          },
        },
        paper: {
          backgroundColor: rawColor.volcano.paper,
          "& .MuiAutocomplete-option": {
            color: rawColor.volcano.text.secondary,
          },
        },
        listbox: {
          "& .MuiAutocomplete-option": {
            color: rawColor.volcano.text.secondary,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ".select__control": {
          backgroundColor: `${rawColor.volcano.paper} !important`,
          borderColor: `${rawColor.volcano.divider} !important`,
          "&:hover": {
            borderColor: `${rawColor.volcano.text.secondary} !important`,
          },
          "&--is-focused": {
            borderColor: `${rawColor.volcano.main} !important`,
            boxShadow: `0 0 0 1px ${rawColor.volcano.main} !important`,
          },
        },
        ".select__menu": {
          backgroundColor: `${rawColor.volcano.paper} !important`,
          border: `1px solid ${rawColor.volcano.divider} !important`,
        },
        ".select__option": {
          backgroundColor: `${rawColor.volcano.paper} !important`,
          color: `${rawColor.volcano.text.secondary} !important`,
          "&:hover": {
            backgroundColor: `${rawColor.volcano.sidebar.hover} !important`,
          },
          "&--is-focused": {
            backgroundColor: `${rawColor.volcano.sidebar.hover} !important`,
          },
          "&--is-selected": {
            backgroundColor: `${rawColor.volcano.sidebar.selected} !important`,
            color: `${rawColor.volcano.main} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.volcano.sidebar.selected} !important`,
            },
          },
        },
        ".select__multi-value": {
          backgroundColor: `${rawColor.volcano.sidebar.hover} !important`,
          "&__label": {
            color: `${rawColor.volcano.text.secondary} !important`,
          },
          "&__remove": {
            color: `${rawColor.volcano.text.secondary} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.volcano.sidebar.selected} !important`,
              color: `${rawColor.volcano.main} !important`,
            },
          },
        },
        ".select__placeholder": {
          color: `${rawColor.volcano.text.secondary} !important`,
        },
        ".select__single-value": {
          color: `${rawColor.volcano.text.secondary} !important`,
        },
        ".select__input": {
          color: `${rawColor.volcano.text.secondary} !important`,
        },
        ".select__indicator": {
          color: `${rawColor.volcano.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.volcano.main} !important`,
          },
        },
        ".select__indicator-separator": {
          backgroundColor: `${rawColor.volcano.divider} !important`,
        },
        ".select__clear-indicator": {
          color: `${rawColor.volcano.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.volcano.main} !important`,
          },
        },
        ".select__dropdown-indicator": {
          color: `${rawColor.volcano.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.volcano.main} !important`,
          },
        },
      },
    },
  },
};

export const volcanoTheme = createTheme(volcanoThemeOptions);
